import React, { useRef, useState } from "react";
import { signal } from "@preact/signals-react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { fireTagManagerEvent } from "@/services/tagmanager";
import {
  BookingComSearchRegions,
  Destination,
  getPaginated,
} from "@/services/collection";
import { Button } from "@/components/ui/button";

export const SelectedHotel = signal({
  dest_label: "",
  dest_id: "",
  dest_name: "",
  search_type: "",
  latitude: "",
  longitude: "",
});

type Props = {
  onSubmit: () => void;
  buttonLabel: string;
};

const SelectHotelInput = (props: Props) => {
  const { onSubmit, buttonLabel } = props;
  const { t } = useTranslation();
  const [alreadyExist, setAlreadyExist] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isSelectLoading, setIsSelectLoading] = useState(false);

  const [locations, setLocations] = useState<Destination[]>([]);

  const searchLocation = async (query: string) => {
    setIsSelectLoading(true);
    try {
      const data = await BookingComSearchRegions({
        query,
      });
      // List of European country codes
      const europeanCountryCodes = [
        "at",
        "be",
        "bg",
        "hr",
        "cy",
        "cz",
        "dk",
        "ee",
        "fi",
        "fr",
        "de",
        "gr",
        "hu",
        "ie",
        "it",
        "lv",
        "lt",
        "lu",
        "mt",
        "nl",
        "pl",
        "pt",
        "ro",
        "sk",
        "si",
        "es",
        "se",
        "gb",
        "ch",
        "no",
        "is",
        "li",
        "mc",
        "sm",
        "va",
        "ad",
        "me",
        "mk",
        "rs",
        "al",
      ];

      // Filter for hotels, and in production also filter for European countries
      const filteredHotels = data.filter(
        (item: Destination) =>
          item.search_type === "hotel" &&
          (import.meta.env.PROD
            ? europeanCountryCodes.includes(item.cc1.toLowerCase())
            : true)
      );
      // Sort results to put Italian hotels at the top
      const sortedHotels = filteredHotels.sort((a, b) => {
        // If a is Italian and b is not, a comes first
        if (a.cc1.toLowerCase() === "it" && b.cc1.toLowerCase() !== "it") {
          return -1;
        }
        // If b is Italian and a is not, b comes first
        if (b.cc1.toLowerCase() === "it" && a.cc1.toLowerCase() !== "it") {
          return 1;
        }
        // Otherwise maintain original order
        return 0;
      });
      setLocations(sortedHotels);
      setIsSelectLoading(false);
    } catch (error) {
      console.log(error);
    }
    setIsSelectLoading(false);
  };

  const searchCallbackDebounced = useRef(debounce(searchLocation, 1000));
  const searchEventFired = useRef(false);

  const checkAlreadyExist = async (dest_id: string) => {
    try {
      const result = await getPaginated({
        page: 1,
        size: 1,
        query: JSON.stringify({ "preData.bookingComId": dest_id }),
      });
      if (result.data.length > 0) {
        setAlreadyExist(true);
        SelectedHotel.value = {
          dest_label: "",
          dest_name: "",
          dest_id: "",
          search_type: "",
          latitude: "",
          longitude: "",
        };
      }
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <>
      <div className="my-10">
        <input
          type="text"
          placeholder={t("onboarding.destination")}
          value={SelectedHotel.value.dest_label}
          onChange={(e) => {
            setAlreadyExist(false);
            SelectedHotel.value = {
              ...SelectedHotel.value,
              dest_label: e.target.value,
            };
            setIsLocationOpen(true);
            if (e.target.value.length >= 2) {
              setIsSelectLoading(true);
              searchCallbackDebounced.current(e.target.value);

              if (!searchEventFired.current) {
                searchEventFired.current = true;
                fireTagManagerEvent("dashboard_hotel_search_start");
              }
            }
          }}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-takyon"
        />
        {alreadyExist && (
          <p className="text-xs font-medium mt-1 text-red-500">
            {t("onboarding.alreadyExist")}
          </p>
        )}
        {SelectedHotel.value.dest_label && (
          <button
            onClick={() => {
              SelectedHotel.value = {
                ...SelectedHotel.value,
                dest_label: "",
              };
              setLocations([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <div className="h-4 w-4 text-gray-500"></div>
          </button>
        )}
        {isLocationOpen && SelectedHotel.value.dest_label.length >= 2 && (
          <div className="absolute mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto z-50">
            {isSelectLoading ? (
              <div className="p-2 space-y-2">
                <div className="h-5 bg-gray-100 rounded animate-pulse" />
                <div className="h-5 bg-gray-100 rounded animate-pulse" />
                <div className="h-5 bg-gray-100 rounded animate-pulse" />
              </div>
            ) : locations.length > 0 ? (
              <div className="py-1">
                {locations.map((location) => (
                  <button
                    key={location.dest_id}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    onClick={() => {
                      checkAlreadyExist(location.dest_id);
                      SelectedHotel.value = {
                        ...SelectedHotel.value,
                        dest_label: location.label,
                        dest_id: location.dest_id,
                        dest_name: location.name,
                        search_type: location.dest_type,
                        latitude: "",
                        longitude: "",
                      };
                      // Salva nel sessionStorage
                      sessionStorage.setItem(
                        "selectedHotel",
                        JSON.stringify(SelectedHotel.value)
                      );
                      setIsLocationOpen(false);
                    }}
                  >
                    <div>
                      <div className="text-sm font-medium">
                        {location.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {location.city_name || location.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p
                className="p-4 text-center text-gray-400 cursor-pointer"
                onClick={() =>
                  (window.location.href = `mailto:partner.support@takyon.io?subject=${t(
                    "auth.contactus_subject"
                  )}&body=${t("auth.contactus_body")} ${
                    SelectedHotel.value.dest_label
                  }`)
                }
              >
                {t("onboarding.cantFIndIt")}{" "}
                <span className="font-medium">{t("onboarding.contactus")}</span>
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <Button
          variant="takyon"
          onClick={() => {
            fireTagManagerEvent("dashboard_hotel_selected", {
              name: SelectedHotel.value.dest_name,
            });
            onSubmit();
          }}
          disabled={!SelectedHotel.value.dest_id}
        >
          {buttonLabel}
        </Button>
      </div>
    </>
  );
};

export default SelectHotelInput;
